import { Browser } from '@capacitor/browser';
import { platform } from '@shared/lib/platform';

/**
 * The one way the app opens a link.
 *
 * Native opens the in-app browser (SFSafariViewController / Chrome Custom Tabs), which
 * keeps the member's zoom and text size; `window.open` is a no-op in the native WebView.
 * The web build opens a new tab. Mail and phone links hand off to the OS.
 */
export async function openLink(url: string): Promise<void> {
  // Links come from tenant configuration: only web, mail and phone links may open.
  if (!/^(https?|mailto|tel):/i.test(url)) throw new Error('Unsupported link');

  if (/^(mailto|tel):/i.test(url)) {
    window.location.href = url;
    return;
  }

  if (platform.isNative()) {
    await Browser.open({ url, presentationStyle: 'popover' });
    return;
  }

  window.open(url, '_blank', 'noopener');
}

/**
 * Native only: open a link in the in-app browser and resolve once the member closes it. For
 * hosted payment pages, where the app carries on (reconciles the result) after the browser is gone.
 */
export async function openLinkUntilClosed(url: string): Promise<void> {
  let onClosed!: () => void;
  const closed = new Promise<void>((resolve) => {
    onClosed = resolve;
  });

  const listener = await Browser.addListener('browserFinished', onClosed);
  try {
    await openLink(url);
    await closed;
  } finally {
    await listener.remove();
  }
}
